import {
	type ErrorResponse,
	isRouteErrorResponse,
	useParams,
	useRouteError,
} from '@remix-run/react'
import { captureRemixErrorBoundaryError } from '@sentry/remix'
import { getErrorMessage } from '#app/utils/misc.tsx'
import {
	Alert,
	AlertDescription,
	AlertTitle,
} from '#app/components/ui/alert.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
type StatusHandler = (info: {
	error: ErrorResponse
	params: Record<string, string | undefined>
}) => JSX.Element | null

export function GeneralErrorBoundary({
	defaultStatusHandler = ({ error }) => (
		<Alert variant="destructive">
			<Icon name="exclamation-triangle" />
			<AlertTitle>Error {error.status}</AlertTitle>
			<AlertDescription>{error.data.message || error.data}</AlertDescription>
		</Alert>
	),
	statusHandlers = {
		403: ({ error }) => (
			<Alert variant="destructive">
				<Icon name="exclamation-triangle" />
				<AlertTitle>Access Denied</AlertTitle>
				<AlertDescription>
					{error.data.message ||
						'You do not have permission to view this page.'}
				</AlertDescription>
			</Alert>
		),
	},
	unexpectedErrorHandler = (error: unknown) => (
		<Alert variant="destructive">
			<Icon name="exclamation-triangle" />
			<AlertTitle>Error</AlertTitle>
			<AlertDescription>{getErrorMessage(error)}</AlertDescription>
		</Alert>
	),
}: {
	defaultStatusHandler?: StatusHandler
	statusHandlers?: Record<number, StatusHandler>
	unexpectedErrorHandler?: (error: unknown) => JSX.Element | null
}) {
	const error = useRouteError()
	captureRemixErrorBoundaryError(error)
	const params = useParams()

	if (typeof document !== 'undefined') {
		console.error(error)
	}

	return (
		<div className="container flex items-center justify-center pt-20">
			{isRouteErrorResponse(error)
				? (statusHandlers?.[error.status] ?? defaultStatusHandler)({
						error,
						params,
					})
				: unexpectedErrorHandler(error)}
		</div>
	)
}
